import { useWindowDimensions, View } from "@-/com.npmjs.react-native";
import Animated, {
  cancelAnimation,
  Easing,
  runOnJS,
  type SharedValue,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { formatHanAmount, formatHanMilesAmount } from "@-/dev.improve.haniverse.lib.han-currency";
import useLatest from "@-/com.npmjs.use-latest";
import { use, useEffect, useMemo, useRef, useState } from "react";
import {
  type ActiveHanCoinDeposit,
  HanCoinDepositContext,
  HanCoinDepositsContext,
} from "#han-coin-deposit-context";
import {
  playHanCoinBurstFeedback,
  playHanCoinDepositedFeedback,
  playHanCoinLandedFeedback,
} from "#han-coin-deposit-feedback";
import { HanCoinGlyph } from "#han-coin-glyph";
import {
  HAN_COIN_SIZE,
  hanCoinBurstPoint,
  type HanCoinFlight,
  planHanCoinDeposit,
  type WindowPoint,
} from "#han-coin-deposit-plan";

/** How long the counter stays beside its target after the last coin lands. */
const COUNTER_LINGER_MS = 1100;
const COUNTER_HEIGHT = 32;
const SPARKLE_COUNT = 10;
/** With reduced motion there is no flight: the counter shows the result for this long. */
const REDUCED_MOTION_DURATION_MS = 1800;
/** Past the planned landing and linger, a run is removed whether or not its coins all landed. */
const RUN_DEADLINE_SLACK_MS = 2000;

function FlyingHanCoin({
  coin,
  onLanded,
  pillPop,
  ring,
  targetPulse,
}: {
  coin: HanCoinFlight;
  onLanded: (han: number) => void;
  pillPop: SharedValue<number>;
  ring: SharedValue<number>;
  targetPulse: SharedValue<number> | null;
}) {
  const burst = useSharedValue(0);
  const flight = useSharedValue(0);
  useEffect(() => {
    burst.set(
      withDelay(
        coin.burstDelayMs,
        withTiming(1, { duration: 420, easing: Easing.out(Easing.cubic) }),
      ),
    );
    flight.set(
      withDelay(
        coin.flightDelayMs,
        // Accelerating all the way in, like a coin dropped into a slot.
        withTiming(1, { duration: coin.flightMs, easing: Easing.in(Easing.quad) }, (finished) => {
          if (!finished) {
            return;
          }
          targetPulse?.set(
            withSequence(
              withTiming(1, { duration: 60 }),
              withSpring(0, { damping: 7, mass: 0.6, stiffness: 260 }),
            ),
          );
          pillPop.set(withSequence(withTiming(1, { duration: 50 }), withSpring(0, { damping: 8 })));
          ring.set(0);
          ring.set(withTiming(1, { duration: 420, easing: Easing.out(Easing.quad) }));
          runOnJS(onLanded)(coin.han);
        }),
      ),
    );
    return () => {
      cancelAnimation(burst);
      cancelAnimation(flight);
    };
  }, [burst, coin, flight, onLanded, pillPop, ring, targetPulse]);
  const style = useAnimatedStyle(() => {
    const b = burst.get();
    const u = flight.get();
    const from = hanCoinBurstPoint(coin, b);
    const v = 1 - u;
    const x = v * v * from.x + 2 * v * u * coin.control.x + u * u * coin.target.x;
    const y = v * v * from.y + 2 * v * u * coin.control.y + u * u * coin.target.y;
    // Pops out past full size and settles, then shrinks as it drops into the button.
    const pop = b < 0.55 ? 0.25 + (b / 0.55) * 0.95 : 1.2 - ((b - 0.55) / 0.45) * 0.2;
    const turns = b * 0.5 + u * coin.spins;
    return {
      opacity: b === 0 ? 0 : u < 0.9 ? 1 : (1 - u) / 0.1,
      transform: [
        { translateX: x - HAN_COIN_SIZE / 2 },
        { translateY: y - HAN_COIN_SIZE / 2 },
        { perspective: 600 },
        { rotateY: `${turns * 360}deg` },
        { rotateZ: `${coin.tilt}deg` },
        { scale: pop * (1 - 0.5 * u) },
      ],
    };
  });
  return (
    <Animated.View className="absolute top-0 left-0" style={style}>
      <HanCoinGlyph size={HAN_COIN_SIZE} />
    </Animated.View>
  );
}

/** A shockwave around the target (the drawer button, or the sheet's Han), restarted by every landing. */
function TargetRing({
  ring,
  target,
}: {
  ring: SharedValue<number>;
  target: ActiveHanCoinDeposit["target"];
}) {
  const style = useAnimatedStyle(() => {
    const progress = ring.get();
    return {
      opacity: progress <= 0 || progress >= 1 ? 0 : 0.75 * (1 - progress),
      transform: [{ scale: 1 + 0.65 * progress }],
    };
  });
  return (
    <Animated.View
      className="absolute rounded-full border-2 border-amber-400"
      style={[
        {
          height: target.size,
          left: target.x - target.size / 2,
          top: target.y - target.size / 2,
          width: target.size,
        },
        style,
      ]}
    />
  );
}

/** Golden glints thrown off the target when the deposit completes. */
function DepositSparkle({ center, index }: { center: WindowPoint; index: number }) {
  const progress = useSharedValue(0);
  useEffect(() => {
    progress.set(withTiming(1, { duration: 560, easing: Easing.out(Easing.cubic) }));
  }, [progress]);
  const angle = (index / SPARKLE_COUNT) * Math.PI * 2 + (index % 2) * 0.3;
  const distance = 24 + (index % 3) * 9;
  const style = useAnimatedStyle(() => {
    const p = progress.get();
    return {
      opacity: 1 - p,
      transform: [
        { translateX: center.x - 3 + Math.cos(angle) * distance * p },
        { translateY: center.y - 3 + Math.sin(angle) * distance * p },
        { rotateZ: "45deg" },
        { scale: 1.4 - p },
      ],
    };
  });
  return (
    <Animated.View
      className={
        index % 2 === 0
          ? "absolute top-0 left-0 size-1.5 bg-amber-300"
          : "absolute top-0 left-0 size-1.5 bg-white"
      }
      style={style}
    />
  );
}

/** What a deposit brings besides Han: `+1,000` HanMiles, `+1 per review today`, `+1 龙`. */
function DepositExtras({
  deposit,
  size,
  textClassName,
}: {
  deposit: ActiveHanCoinDeposit;
  size: number;
  textClassName: string;
}) {
  return (
    <>
      {deposit.hanMiles > 0 ? (
        <View className="flex-row items-center gap-0.5">
          <Icon className={textClassName} name="MdFlightTakeoff" size={size} />
          <Text className={textClassName}>+{formatHanMilesAmount(deposit.hanMiles)}</Text>
        </View>
      ) : null}
      {deposit.reviewBonusHan > 0 ? (
        <View className="flex-row items-center gap-1">
          <HanCoinGlyph size={size} />
          <Text className={textClassName}>
            +{formatHanAmount(deposit.reviewBonusHan)}
            <Text className="font-sans text-sm font-semibold"> per review today</Text>
          </Text>
        </View>
      ) : null}
      {deposit.dragons > 0 ? <Text className={textClassName}>+{deposit.dragons} 龙</Text> : null}
    </>
  );
}

/** "+88" popping out of the source, then floating away. */
function DepositAmountLabel({ deposit }: { deposit: ActiveHanCoinDeposit }) {
  const progress = useSharedValue(0);
  const pop = useSharedValue(0);
  useEffect(() => {
    pop.set(withSpring(1, { damping: 9, stiffness: 220 }));
    progress.set(withTiming(1, { duration: 1400, easing: Easing.out(Easing.quad) }));
  }, [pop, progress]);
  const style = useAnimatedStyle(() => {
    const p = progress.get();
    return {
      opacity: p < 0.65 ? 1 : 1 - (p - 0.65) / 0.35,
      transform: [{ translateY: -40 * p }, { scale: 0.4 + 0.6 * pop.get() }],
    };
  });
  const { source } = deposit;
  return (
    <Animated.View
      className="absolute w-64 items-center"
      style={[{ left: source.x + source.width / 2 - 128, top: source.y - 30 }, style]}
    >
      <View className="flex-row items-center gap-1.5 rounded-full border border-amber-600 bg-amber-300 px-2.5 py-0.5 shadow-sm">
        {deposit.han > 0 ? (
          <View className="flex-row items-center gap-1">
            <HanCoinGlyph size={20} />
            <Text className="font-sans text-xl font-bold text-amber-950">+{deposit.han}</Text>
          </View>
        ) : null}
        <DepositExtras
          deposit={deposit}
          size={20}
          textClassName="font-sans text-xl font-bold text-amber-950"
        />
      </View>
    </Animated.View>
  );
}

/**
 * The wallet beside the drawer button, counting up as each coin lands. It slides out of the
 * button with the first coin and back into it once the deposit is banked.
 */
function DepositCounter({
  deposit,
  landedHan,
  pillPop,
  shown,
  windowWidth,
}: {
  deposit: ActiveHanCoinDeposit;
  landedHan: number;
  pillPop: SharedValue<number>;
  shown: SharedValue<number>;
  windowWidth: number;
}) {
  const { target } = deposit;
  // Beside the button, on the side facing the middle of the screen.
  const opensLeft = target.x > windowWidth / 2;
  const style = useAnimatedStyle(() => {
    const visible = shown.get();
    return {
      opacity: visible,
      transform: [
        { translateX: (opensLeft ? 14 : -14) * (1 - visible) },
        { scale: (0.85 + 0.15 * visible) * (1 + 0.12 * pillPop.get()) },
      ],
    };
  });
  const value =
    deposit.balanceAfter === null
      ? `+${formatHanAmount(landedHan)}`
      : formatHanAmount(deposit.balanceAfter.han - deposit.han + landedHan);
  return (
    <Animated.View
      className="absolute flex-row items-center gap-1.5 rounded-full border border-amber-500/60 bg-background px-3 shadow-md"
      style={[
        {
          height: COUNTER_HEIGHT,
          top: target.y - COUNTER_HEIGHT / 2,
          ...(opensLeft
            ? { right: windowWidth - (target.x - target.size / 2 - 8) }
            : { left: target.x + target.size / 2 + 8 }),
        },
        style,
      ]}
      testID="han-coin-deposit-counter"
    >
      {deposit.han > 0 ? (
        <>
          <HanCoinGlyph size={18} />
          <Text className="font-sans text-base font-semibold text-foreground tabular-nums">
            {value}
          </Text>
        </>
      ) : null}
      {landedHan >= deposit.han ? (
        <DepositExtras
          deposit={deposit}
          size={18}
          textClassName="font-sans text-base font-semibold text-foreground"
        />
      ) : null}
    </Animated.View>
  );
}

/** Kicks a target's bump: to 1 and springing back. */
function bumpTarget(pulse: SharedValue<number> | null) {
  pulse?.set(
    withSequence(
      withTiming(1, { duration: 60 }),
      withSpring(0, { damping: 7, mass: 0.6, stiffness: 260 }),
    ),
  );
}

function HanCoinDepositRun({
  deposit,
  isMuted,
  landHan,
  onDone,
  windowWidth,
}: {
  deposit: ActiveHanCoinDeposit;
  isMuted: boolean;
  landHan: (id: number, han: number) => void;
  onDone: (id: number) => void;
  windowWidth: number;
}) {
  const { targetPulses } = use(HanCoinDepositContext);
  const isWallet = deposit.landsIn === "wallet";
  const targetPulse = targetPulses?.[isWallet ? "wallet-han" : "drawer"] ?? null;
  // HanMiles do not fly: the sheet's HanMiles bump once the deposit is banked.
  const hanMilesPulse =
    isWallet && deposit.hanMiles > 0 ? (targetPulses?.["wallet-han-miles"] ?? null) : null;
  // Only Han flies in as coins. With reduced motion, or nothing but dragons, HanMiles or a review
  // bonus to bank, the counter simply shows the result.
  const isStill = useReducedMotion() || deposit.han <= 0;
  const plan = useMemo(
    () =>
      planHanCoinDeposit({
        han: deposit.han,
        seed: deposit.id * 7919 + deposit.han,
        source: deposit.source,
        target: deposit.target,
      }),
    [deposit.han, deposit.id, deposit.source, deposit.target],
  );
  const ring = useSharedValue(0);
  const pillPop = useSharedValue(0);
  const counterShown = useSharedValue(0);
  const landedHan = isStill ? deposit.han : deposit.landedHan;
  const [isBanked, setIsBanked] = useState(false);
  const landedCount = useRef(0);
  const isMutedRef: { readonly current: boolean } = useLatest(isMuted);
  const onDoneRef: { readonly current: (id: number) => void } = useLatest(onDone);

  useEffect(() => {
    if (isStill) {
      playHanCoinDepositedFeedback({ isMuted: isMutedRef.current });
      bumpTarget(hanMilesPulse);
      counterShown.set(withTiming(1, { duration: 160 }));
      const hide = setTimeout(() => counterShown.set(withTiming(0, { duration: 200 })), 1500);
      const done = setTimeout(() => onDoneRef.current(deposit.id), REDUCED_MOTION_DURATION_MS);
      return () => {
        clearTimeout(hide);
        clearTimeout(done);
      };
    }
    playHanCoinBurstFeedback({ isMuted: isMutedRef.current });
    // Normally the last landing ends the run; this ends it even if a coin's flight never finishes.
    const deadline = setTimeout(
      () => onDoneRef.current(deposit.id),
      plan.durationMs + COUNTER_LINGER_MS + RUN_DEADLINE_SLACK_MS,
    );
    return () => clearTimeout(deadline);
  }, [counterShown, deposit.id, hanMilesPulse, isMutedRef, isStill, onDoneRef, plan.durationMs]);

  useEffect(() => {
    if (!isBanked) {
      return;
    }
    const hide = setTimeout(
      () => counterShown.set(withTiming(0, { duration: 220 })),
      COUNTER_LINGER_MS,
    );
    const done = setTimeout(() => onDoneRef.current(deposit.id), COUNTER_LINGER_MS + 600);
    return () => {
      clearTimeout(hide);
      clearTimeout(done);
    };
  }, [counterShown, deposit.id, isBanked, onDoneRef]);

  const onLanded = useMemo(
    () => (han: number) => {
      const index = landedCount.current;
      landedCount.current += 1;
      const total = plan.coins.length;
      if (index === 0) {
        counterShown.set(withTiming(1, { duration: 140 }));
      }
      landHan(deposit.id, han);
      playHanCoinLandedFeedback({ index, isMuted: isMutedRef.current, total });
      if (index === total - 1) {
        playHanCoinDepositedFeedback({ isMuted: isMutedRef.current });
        bumpTarget(hanMilesPulse);
        setIsBanked(true);
      }
    },
    [counterShown, deposit.id, hanMilesPulse, isMutedRef, landHan, plan.coins.length],
  );

  return (
    <>
      {isStill ? null : (
        <>
          <TargetRing ring={ring} target={deposit.target} />
          {plan.coins.map((coin, index) => (
            <FlyingHanCoin
              coin={coin}
              // oxlint-disable-next-line react/no-array-index-key -- a run's coins never reorder.
              key={index}
              onLanded={onLanded}
              pillPop={pillPop}
              ring={ring}
              targetPulse={targetPulse}
            />
          ))}
          {isBanked
            ? Array.from({ length: SPARKLE_COUNT }, (_, index) => (
                // oxlint-disable-next-line react/no-array-index-key -- fixed, positional sparkles.
                <DepositSparkle center={deposit.target} index={index} key={index} />
              ))
            : null}
        </>
      )}
      <DepositAmountLabel deposit={deposit} />
      {/* The sheet's wallet counts up in place (`useHanCoinDepositWalletDisplay`). */}
      {isWallet ? null : (
        <DepositCounter
          deposit={deposit}
          landedHan={landedHan}
          pillPop={pillPop}
          shown={counterShown}
          windowWidth={windowWidth}
        />
      )}
    </>
  );
}

/**
 * Draws the Han coin deposits in flight (see `HanCoinDepositProvider`). Mount it once, above the
 * screens and BELOW the drawer button, so the coins drop in under the button's glass. It never
 * takes a touch.
 */
export function HanCoinDepositOverlay() {
  const { deposits, isMuted, landHan, removeDeposit } = use(HanCoinDepositsContext);
  const { height, width } = useWindowDimensions();
  const rootRef = useRef<View>(null);
  // Deposits are planned in window coordinates; this overlay may not start at the window's origin.
  const [origin, setOrigin] = useState<WindowPoint>({ x: 0, y: 0 });
  return (
    <View
      className="absolute inset-0 z-50"
      onLayout={() => {
        rootRef.current?.measureInWindow((x, y) => {
          setOrigin((current) => (current.x === x && current.y === y ? current : { x, y }));
        });
      }}
      pointerEvents="none"
      ref={rootRef}
      testID="han-coin-deposit-overlay"
    >
      {deposits.length === 0 ? null : (
        <View className="absolute" style={{ height, left: -origin.x, top: -origin.y, width }}>
          {deposits.map((deposit) => (
            <HanCoinDepositRun
              deposit={deposit}
              isMuted={isMuted}
              key={deposit.id}
              landHan={landHan}
              onDone={removeDeposit}
              windowWidth={width}
            />
          ))}
        </View>
      )}
    </View>
  );
}
